import { franc } from "franc-min";
import type { AssistantSegment } from "./assistantSegments.ts";
import { ACTIVITY_THINKING } from "./activityTexts.de.ts";

/**
 * Display guard for model reasoning. Every reasoning block shown in the chat
 * timeline passes through here. German stays as written. Anything else is
 * translated, and English is never rendered if that translation fails.
 */

export const REASONING_TRANSLATE_TIMEOUT_MS = 3_000;

const EN_STOP = new Set(
  "the and is to i let need should this a of for with that it be we you from on in are was have has just so but or not can will if my an as at by do did".split(
    " ",
  ),
);
const DE_STOP = new Set(
  "der die das und ist ich nicht zu wir ein eine den dem des im auf für mit von dass auch wenn dann aber bei nach noch hier wie was ob nur man schon sie es zum zur vom".split(
    " ",
  ),
);
const ABBREV = new Set(
  "z b bzw dh d h u ua ca nr dr hr fr ggf evtl inkl exkl etc eg e g ie i vs mr mrs usw abs mio vgl sog zzgl jew bsp".split(
    " ",
  ),
);

const KEEPER_SOURCES = [
  /```[\s\S]*?```/g,
  /`[^`\n]+`/g,
  /https?:\/\/[^\s<>)]+/gi,
  /\b(?:[A-Za-z0-9._-]+\/)+[A-Za-z0-9._-]+\b/g,
  /\b[A-Za-z][A-Za-z0-9]*_[A-Za-z0-9_]+\b/g,
  /\b[a-z][a-z0-9]*[A-Z][A-Za-z0-9]*\b/g,
  /\b[A-Za-z0-9._-]+\.(?:tsx?|jsx?|mjs|cjs|py|md|json|sql|pdf|xml|csv|png|jpe?g|svg|html|css|vue|go|rs|txt)\b/gi,
  /\b[A-Z][A-Z0-9]+\b/g,
];

type Range = { start: number; end: number };
type Span = { kind: "keep" | "prose"; text: string };

export type ReasoningBlock = { id: string; text: string; done: boolean };

export type ReasoningSnapshot = { text: string; settled: boolean };

export type TranslateActivityText = (text: string) => Promise<string>;

type Piece = {
  generation: number;
  display: string | null;
  promise?: Promise<string>;
};

type BlockState = {
  raw: string;
  consumed: number;
  done: boolean;
  generation: number;
  pieces: Piece[];
};

const sharedCache = new Map<string, string>();
const sharedInflight = new Map<string, Promise<string>>();

function protectedRanges(text: string): Range[] {
  const found: Range[] = [];
  for (const source of KEEPER_SOURCES) {
    const re = new RegExp(source.source, source.flags);
    let match: RegExpExecArray | null;
    while ((match = re.exec(text))) {
      const start = match.index;
      let end = start + match[0].length;
      while (end > start && /[.,;:!?)]/.test(text[end - 1] ?? "")) end -= 1;
      if (end > start) found.push({ start, end });
      if (match[0].length === 0) re.lastIndex += 1;
    }
  }
  found.sort((a, b) => a.start - b.start || b.end - a.end);
  const merged: Range[] = [];
  for (const range of found) {
    const last = merged[merged.length - 1];
    if (last && range.start < last.end) continue;
    merged.push(range);
  }
  return merged;
}

function maskForDetection(text: string): string {
  const ranges = protectedRanges(text);
  if (ranges.length === 0) return text;
  let out = "";
  let cursor = 0;
  for (const range of ranges) {
    out += text.slice(cursor, range.start);
    out += " ";
    cursor = range.end;
  }
  out += text.slice(cursor);
  return out;
}

function spansOf(text: string): Span[] {
  const ranges = protectedRanges(text);
  const spans: Span[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) spans.push({ kind: "prose", text: text.slice(cursor, range.start) });
    spans.push({ kind: "keep", text: text.slice(range.start, range.end) });
    cursor = range.end;
  }
  if (cursor < text.length) spans.push({ kind: "prose", text: text.slice(cursor) });
  return spans;
}

function stopwordCounts(masked: string): { de: number; en: number } {
  const words = masked.toLowerCase().match(/[a-zäöüß]+/g) ?? [];
  let de = 0;
  let en = 0;
  for (const word of words) {
    if (DE_STOP.has(word)) de += 1;
    if (EN_STOP.has(word)) en += 1;
  }
  return { de, en };
}

function detectLang(masked: string): "deu" | "eng" | "und" {
  const sample = masked.replace(/\s+/g, " ").trim();
  if (sample.length < 10) return "und";
  const lang = franc(sample, { only: ["deu", "eng"], minLength: 10 });
  if (lang === "deu" || lang === "eng") return lang;
  return "und";
}

/** Confident German is shown unchanged. Uncertain and non-German text is translated. */
export function classifyActivitySegment(text: string): "show" | "translate" {
  const masked = maskForDetection(text).replace(/\s+/g, " ").trim();
  if (!/[a-zA-ZäöüÄÖÜß]{2,}/.test(masked)) return "show";
  const { de, en } = stopwordCounts(masked);
  const lang = detectLang(masked);
  const stopDe = de > 0 && de > en;
  const stopEn = en > 0 && en >= de;
  if (stopEn || (lang === "eng" && !stopDe)) return "translate";
  if (stopDe && lang !== "eng") return "show";
  if (lang === "deu" && en === 0) return "show";
  return "translate";
}

/** True when an error string is English and must not be shown as-is. */
export function isConfidentEnglish(text: string): boolean {
  const masked = maskForDetection(text).replace(/\s+/g, " ").trim();
  if (!masked) return false;
  const { de, en } = stopwordCounts(masked);
  if (de > en) return false;
  if (/[äöüÄÖÜß]/.test(text) && en === 0) return false;
  if (/\b(error|failed|failure|timeout|invalid|undefined|network|loading|retry)\b/i.test(masked) && de === 0) {
    return true;
  }
  const lang = detectLang(masked);
  if (en > 0 && en >= de && lang !== "deu") return true;
  return lang === "eng" && en >= de;
}

function wordBefore(text: string, index: number): string {
  let cursor = index - 1;
  while (cursor >= 0 && /[A-Za-zÄÖÜäöüß]/.test(text[cursor] ?? "")) cursor -= 1;
  return text.slice(cursor + 1, index).toLowerCase();
}

function isSentenceBoundary(text: string, index: number, flush: boolean): boolean {
  const ch = text[index];
  if (ch !== "." && ch !== "!" && ch !== "?" && ch !== "…") return false;
  if (ch === "." && /\d/.test(text[index - 1] ?? "") && /\d/.test(text[index + 1] ?? "")) return false;
  if (ch === "." && ABBREV.has(wordBefore(text, index))) return false;
  const next = text[index + 1];
  if (next == null) return flush;
  return /\s/.test(next);
}

function findPieceEnd(text: string, from: number, flush: boolean): number | null {
  const ranges = protectedRanges(text);
  const inside = (index: number) => ranges.some((range) => index >= range.start && index < range.end);
  for (let i = from; i < text.length; i += 1) {
    if (inside(i)) continue;
    if (text[i] === "\n") return i + 1;
    if (text.startsWith("...", i)) {
      const end = i + 3;
      const next = text[end];
      if (next == null) return flush ? end : null;
      if (/\s/.test(next)) return end;
      continue;
    }
    if (isSentenceBoundary(text, i, flush)) return i + 1;
  }
  return null;
}

function withTimeout<T>(promise: Promise<T>, ms: number | null): Promise<T> {
  if (ms === null) return promise;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("timeout")), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function sameText(left: string, right: string): boolean {
  return left.replace(/\s+/g, " ").trim().toLowerCase() === right.replace(/\s+/g, " ").trim().toLowerCase();
}

function defaultLog(original: string, translation: string): void {
  if (import.meta.env?.DEV === true) {
    console.info("[reasoning-guard]", { original, translation });
  }
}

export function createReasoningLanguageGuard(options: {
  translate: TranslateActivityText;
  /** null: the translator enforces its own timeout (e.g. measured from send, not from queueing). */
  timeoutMs?: number | null;
  log?: (original: string, translation: string) => void;
  cache?: Map<string, string>;
  inflight?: Map<string, Promise<string>>;
}) {
  const timeoutMs = options.timeoutMs === undefined ? REASONING_TRANSLATE_TIMEOUT_MS : options.timeoutMs;
  const log = options.log ?? defaultLog;
  const cache = options.cache ?? sharedCache;
  const inflight = options.inflight ?? sharedInflight;
  const blocks = new Map<string, BlockState>();
  let listener: (() => void) | null = null;

  const remember = (key: string, value: string) => {
    if (cache.size > 500) {
      const oldest = cache.keys().next().value;
      if (oldest) cache.delete(oldest);
    }
    cache.set(key, value);
  };

  /** `failed`: the placeholder stands in for the prose and must not be cached. */
  const translateProse = async (prose: string): Promise<{ text: string; failed: boolean }> => {
    const lead = prose.match(/^\s*/)?.[0] ?? "";
    const trail = prose.match(/\s*$/)?.[0] ?? "";
    const core = prose.slice(lead.length, prose.length - trail.length);
    if (!core.trim() || classifyActivitySegment(core) === "show") return { text: prose, failed: false };
    const fallback = { text: `${lead}${ACTIVITY_THINKING}${trail}`, failed: true };
    try {
      const translated = (await withTimeout(options.translate(core), timeoutMs)).trim();
      if (!translated || sameText(translated, core) || isConfidentEnglish(translated)) return fallback;
      log(core, translated);
      return { text: `${lead}${translated}${trail}`, failed: false };
    } catch {
      return fallback;
    }
  };

  const renderUncached = async (raw: string): Promise<{ display: string; failed: boolean }> => {
    const rendered: string[] = [];
    let failed = false;
    for (const span of spansOf(raw)) {
      if (span.kind === "keep" || !span.text.trim() || classifyActivitySegment(span.text) === "show") {
        rendered.push(span.text);
        continue;
      }
      const result = await translateProse(span.text);
      failed ||= result.failed;
      rendered.push(result.text);
    }
    return { display: rendered.join(""), failed };
  };

  const renderPiece = (raw: string): Promise<string> => {
    const cached = cache.get(raw);
    if (cached !== undefined) return Promise.resolve(cached);
    const pending = inflight.get(raw);
    if (pending) return pending;
    // A failed piece is not remembered, so reopening the reasoning retries it.
    const next = renderUncached(raw).then(({ display, failed }) => {
      if (!failed) remember(raw, display);
      inflight.delete(raw);
      return display;
    });
    inflight.set(raw, next);
    return next;
  };

  const pushPiece = (state: BlockState, raw: string) => {
    const piece: Piece = { generation: state.generation, display: null };
    state.pieces.push(piece);
    if (!raw.trim() || classifyActivitySegment(raw) === "show") {
      piece.display = raw;
      return;
    }
    const cached = cache.get(raw);
    if (cached !== undefined) {
      piece.display = cached;
      return;
    }
    piece.promise = renderPiece(raw).then((display) => {
      if (piece.generation !== state.generation) return display;
      piece.display = display;
      listener?.();
      return display;
    });
  };

  const ingest = (block: ReasoningBlock) => {
    let state = blocks.get(block.id);
    const consumedPrefix = state ? state.raw.slice(0, state.consumed) : "";
    if (!state || !block.text.startsWith(consumedPrefix)) {
      state = {
        raw: "",
        consumed: 0,
        done: false,
        generation: (state?.generation ?? 0) + 1,
        pieces: [],
      };
      blocks.set(block.id, state);
    }
    state.raw = block.text;
    state.done = block.done;
    while (state.consumed < block.text.length) {
      const end = findPieceEnd(block.text, state.consumed, block.done);
      if (end == null) {
        if (!block.done) break;
        const tail = block.text.slice(state.consumed);
        state.consumed = block.text.length;
        if (tail) pushPiece(state, tail);
        break;
      }
      const slice = block.text.slice(state.consumed, end);
      state.consumed = end;
      if (slice) pushPiece(state, slice);
    }
  };

  return {
    sync(next: ReasoningBlock[], onUpdate?: () => void) {
      if (onUpdate) listener = onUpdate;
      const seen = new Set(next.map((block) => block.id));
      for (const id of blocks.keys()) {
        if (!seen.has(id)) blocks.delete(id);
      }
      for (const block of next) ingest(block);
    },
    snapshot(): Map<string, ReasoningSnapshot> {
      const out = new Map<string, ReasoningSnapshot>();
      for (const [id, state] of blocks) {
        let text = "";
        let settled = true;
        for (const piece of state.pieces) {
          if (piece.display === null) {
            settled = false;
            break;
          }
          text += piece.display;
        }
        out.set(id, { text, settled });
      }
      return out;
    },
    async whenIdle(): Promise<void> {
      const pending: Array<Promise<string>> = [];
      for (const state of blocks.values()) {
        for (const piece of state.pieces) {
          if (piece.promise) pending.push(piece.promise);
        }
      }
      await Promise.all(pending);
    },
    async settle(next: ReasoningBlock[]): Promise<Map<string, ReasoningSnapshot>> {
      this.sync(next);
      await this.whenIdle();
      return this.snapshot();
    },
  };
}

export type ReasoningLanguageGuard = ReturnType<typeof createReasoningLanguageGuard>;

export function reasoningBlocksFromSegments(
  segments: AssistantSegment[] | undefined,
  flush: boolean,
): ReasoningBlock[] {
  const blocks: ReasoningBlock[] = [];
  let index = 0;
  for (const segment of segments ?? []) {
    if (segment.type !== "reasoning") continue;
    blocks.push({
      id: segment.id?.trim() || `idx-${index}`,
      text: segment.text,
      done: segment.done || flush,
    });
    index += 1;
  }
  return blocks;
}

/** Reasoning that is not displayed and not translated: keep the segments, drop the raw text. */
export function withoutReasoningText(segments: AssistantSegment[] | undefined): AssistantSegment[] {
  return (segments ?? []).map((segment) => (segment.type === "reasoning" ? { ...segment, text: "" } : segment));
}

/** Hides later reasoning until earlier blocks are settled, so English never jumps the queue. */
export function applyReasoningGate(
  segments: AssistantSegment[] | undefined,
  snap: Map<string, ReasoningSnapshot>,
): AssistantSegment[] {
  let blocked = false;
  let index = 0;
  return (segments ?? []).map((segment) => {
    if (segment.type !== "reasoning") return segment;
    const id = segment.id?.trim() || `idx-${index}`;
    index += 1;
    if (blocked) return { ...segment, text: "" };
    const state = snap.get(id);
    if (!state || !state.settled) {
      blocked = true;
      return { ...segment, text: state?.text ?? "" };
    }
    return { ...segment, text: state.text };
  });
}
